import React, { useEffect } from "react";
import { styled } from "@mui/system";
import AlertBlock, { IButtonProps } from "./extra/AlertBlock";
import MultiAccountItem from "./MultiAccountItem";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";
import RaFInviteModal from "../raf/Invite/RaFInviteModal";
import { AccountUsersRes, GetMultiAccountUsersApiResponse } from "src/store/features/api/generated";
import * as amplitude from "@amplitude/analytics-browser";
import { trackEvent } from "src/Amplitude";
import { useUserSlice } from "src/store/features/user.slice";
import api from "src/store/features/api";
import { useLoginRedirect } from "src/utils/hooks/useLoginRedirect";
import { useAppDispatch } from "src/store/store";
import {
  setIsMultiAccountApplication,
  setSelectedCurrency,
} from "src/store/features/settings.slice";
import { resetWithdrawalState } from "src/store/features/withdrawal.slice";
import { baseApi } from "src/store/features/api/base";
import { LinearProgress } from "@mui/material";
import { usePermissionGuard } from "src/PermissionGuard";
import { LoadingDotsWithText } from "../dca/styled-components/LoadingDots";

interface MultiAccountListProps {
  disabled: boolean;
}

const MultiAccountList = styled("div")<MultiAccountListProps>(({ disabled }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
  maxHeight: "40vh",
  overflowY: "scroll",
  pointerEvents: disabled ? "none" : "auto",
  minHeight: "100px",
  "&::-webkit-scrollbar": {
    width: "12px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "none",
  },
  "@media (max-width: 768px)": {
    maxHeight: "100vh",
  },
}));

const Divider = styled("div")(() => ({
  width: "100%",
  minHeight: "1px",
  background: "#E5E7E8",
}));

const LinksWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  alignSelf: "stretch",
  paddingTop: "16px",
}));

const Links = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
}));

interface LinkButtonProps {
  hidden?: boolean;
}
const LinkButton = styled(RouterLink)<LinkButtonProps>(({ hidden }) => ({
  display: hidden ? "none" : "flex",
  padding: "4px 8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  borderRadius: "4px",
  color: "#002035",
  background: "transparent",
  outline: "none",
  border: "none",
  cursor: "pointer",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "17px",
  textDecoration: "none",

  "&:hover": {
    background: "#E5E7E8",
  },
}));

interface MultiAccountListV2Props {
  accountList: GetMultiAccountUsersApiResponse;
}
const MultiAccountListV2 = (props: MultiAccountListV2Props) => {
  const { accountList } = props;
  const isMobile = useIsMobile();
  const isUkUser = useIsUkUserCountry();
  const {
    details: { email },
    raf: { OLD_RAF_SYSTEM },
  } = useUserSlice();
  const [setSelectedUser] = api.useSetSelectedUserMutation();
  const { hasPermission } = usePermissionGuard();
  const [getUserData] = api.useLazyGetUserDatav2Query();
  const [getUserMultiAccountData] = api.useLazyGetMultiAccountUsersQuery();
  const [alertText, setAlertText] = React.useState<string>("");
  const loginRedirect = useLoginRedirect();
  const reduxDispatch = useAppDispatch();
  const history = useHistory();

  const isUnverified = accountList?.every((account) => account.isUnverified);
  const showAlert = isUnverified;
  const accountLength = accountList.length - 1;
  const { data: hasIndividualAccount, isLoading: isLoadingHasIndividualAccount } =
    api.useCheckIndividualTradingAccountByEmailQuery();
  // Entity onboarding is not available for UK clients, so once a UK user has
  // an individual account there is no further account type to add.
  const enableAddAccount = hasPermission(["user"]) && !(isUkUser && hasIndividualAccount);

  const addAccounText = hasIndividualAccount ? "Add Entity Account" : "Add Account";

  const alertButtonProps: IButtonProps = {
    text: enableAddAccount ? addAccounText : "",
    onClick: () => {
      // Enable hidden route and redirect
      if (enableAddAccount) {
        if (hasIndividualAccount) {
          return history.push("/entity-onboarding");
        } else {
          reduxDispatch(setIsMultiAccountApplication(true));
          return history.push("/additional-application");
        }
      }
    },
  };

  useEffect(() => {
    if (isUnverified) {
      setAlertText("Your account has yet to be verified, verify for full access");
      alertButtonProps.text = "Verify";
      alertButtonProps.onClick = () => history.push("/application");
    } else {
      setAlertText(`You have ${accountLength} other accounts with Caleb & Brown`);
      if (accountLength <= 0) {
        setAlertText(`You only have one account with Caleb & Brown.`);
      }
    }
  }, [isUnverified]);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [InviteModalOpen, setInviteModalOpen] = React.useState<boolean>(false);
  const handleInviteModalToggle = () => {
    setInviteModalOpen((prev) => !prev);
  };

  const setAmplitudeUserProperties = (account: AccountUsersRes) => {
    const identifyObj = new amplitude.Identify();
    identifyObj.set("Email ID", `${email}`);
    amplitude.identify(identifyObj);
    trackEvent(`Select_user_account_change`, {
      account,
    });
  };

  const handleChangeAccount = async (account: AccountUsersRes) => {
    setIsLoading(true);
    setAmplitudeUserProperties(account);
    let body = {
      customerId: account.customerId,
    };

    await setSelectedUser({ customerIdDto: body });

    // The persisted withdrawal + Travel-Rule state (form inputs, open-modal
    // flag, banner txId, stage, PII flag) is PROFILE-scoped but lives in
    // login-wide localStorage — carrying it across a profile switch renders a
    // banner for a transfer this profile can't poll/resume/cancel (every call
    // 404s on the ownership guard; prod incident 2026-07-13), and an open
    // withdrawal modal would stay open prefilled with the previous profile's
    // destination address and amount against the new profile's funds. The
    // full reset clears all of it; `useTravelRuleHydrate` restores the banner
    // from the server when the user switches back to the initiating profile.
    // Only on a REAL switch: clicking the already-active profile leaves
    // `customerId` unchanged, so the hydrate hook (keyed on customerId) would
    // never re-reconcile and the cleared banner couldn't come back until a
    // full remount.
    // Derive the active profile from `accountList` (real API flags), NOT the
    // click payload — `MultiAccountItem` hardcodes `selected: true` when it
    // builds the account it passes to onClick, so `account.selected` here is
    // always true and would disable the reset on every switch.
    const activeCustomerId = (
      accountList.find((a) => a.selected) ?? accountList.find((a) => a.default)
    )?.customerId;
    if (account.customerId !== activeCustomerId) {
      reduxDispatch(resetWithdrawalState());
    }

    await getUserData()
      .unwrap()
      .then(async (userData) => {
        if (userData) {
          const defaultCurrency = userData.defaultCurrencyConfig.code;
          reduxDispatch(setSelectedCurrency(defaultCurrency));
        }
      });
    await getUserMultiAccountData();
    reduxDispatch(baseApi.util.resetApiState());
    loginRedirect();
  };

  React.useEffect(() => {
    return () => {
      setIsLoading(false);
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Check multi account status
       * If loading, display loading dots
       * If User don't have an verified indivdual account display 'Add Account' which sends them to account selection
       * If they have a verified individual account, display 'Add Entity Account' which sends users to Sumsub
       */}
      {isLoadingHasIndividualAccount ? (
        <div style={{ marginBottom: "14px" }}>
          <LoadingDotsWithText text="Checking your account status" />
        </div>
      ) : (
        <AlertBlock text={alertText} buttonProps={alertButtonProps} showAlert={showAlert} />
      )}

      {/* Multi-account list + items */}
      <div style={{ flexGrow: 1, overflow: "hidden" }}>
        <MultiAccountList disabled={isLoading}>
          {accountList.map((account) => (
            <MultiAccountItem
              key={account.customerId}
              accountName={account.name}
              customerID={account.customerId}
              isDefault={account.default}
              isUnverified={account.isUnverified}
              selected={account.selected}
              onClick={handleChangeAccount}
            />
          ))}
        </MultiAccountList>
      </div>
      {isLoading ? <LinearProgress color="primary" /> : <Divider />}

      {/* Profile links */}
      <LinksWrapper>
        <Links>
          <LinkButton to="/settings/profile">View Profile</LinkButton>
          <LinkButton to="#" hidden={isUkUser || !isMobile || OLD_RAF_SYSTEM} onClick={handleInviteModalToggle}>
            Refer
          </LinkButton>
          <LinkButton to="/settings/usefulLinks">Help</LinkButton>
        </Links>
      </LinksWrapper>

      {/* RaF Invite Modal*/}
      {!isUkUser && (
        <RaFInviteModal
          open={InviteModalOpen}
          onClose={handleInviteModalToggle}
          fullScreen={isMobile}
        />
      )}
    </div>
  );
};

export default MultiAccountListV2;
